import axios from 'axios';
import {
  clearLocalStoragePreserveMls,
  clearSessionStoragePreserveMls,
} from '../e2ee/mlsStorageUtils.js';
import { realtimeRuntime } from '../actionCable/realtimeRuntime.js';
import { resetReduxStoreAction, setConfirmSignOut } from '../redux/actions';
import store from '../redux/store';
import { getMessengerV2PresenceSessionId } from '../features/messenger-v2/runtime/presenceSession.js';
import {
  CACHE_DEFAULT_TTL_MS,
  buildCachedResponse,
  getCachedValue,
  invalidateCache,
  setCachedValue,
} from './cache';
import { CONSTANTS, getRoomsBasePath } from './constants';
import { invalidateGraveCache, storeGraveInCache } from './graveCache';
import { sanitizeMlsRequestInit } from './mlsTransportSanitizer.js';
import { getBrowserInstallationId } from './browserInstallation.js';

const GRAVE_SHOW_REGEX = /^\/api\/graves\/([^/?#]+)/;
const VISITOR_TOKEN_KEY = 'ng.visitor.token';
export const AUTH_TOKEN_REFRESHED_EVENT = 'nettgrav:auth-token-refreshed';
export const AUTH_TOKEN_REMOVED_EVENT = 'nettgrav:auth-token-removed';
const AUTH_TOKEN_STORAGE_SYNC_KEY = 'ng.auth.token_sync';

const getVisitorToken = () => {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem(VISITOR_TOKEN_KEY);
  } catch {
    return null;
  }
};

const setVisitorToken = (token) => {
  if (typeof window === 'undefined') return;
  try {
    if (token) {
      localStorage.setItem(VISITOR_TOKEN_KEY, token);
    } else {
      localStorage.removeItem(VISITOR_TOKEN_KEY);
    }
  } catch {}
};

const getPayloadGraveSlug = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  const grave = payload.grave && typeof payload.grave === 'object' ? payload.grave : payload;
  return grave.slug || grave.grave_slug || null;
};

const SIGNED_MEDIA_PATTERNS = [
  /X-Amz-Algorithm=AWS4-HMAC-SHA256/i,
  /media\.nettgrav\.com/i,
  /s3\.de\.io\.cloud\.ovh\.net/i,
];

const payloadHasSignedMediaUrl = (payload) => {
  if (!payload) return false;
  try {
    const text = JSON.stringify(payload);
    return SIGNED_MEDIA_PATTERNS.some((pattern) => pattern.test(text));
  } catch {
    return false;
  }
};

const shouldSkipGraveCache = (endpoint, payload) => {
  if (!payload) return false;
  const match = typeof endpoint === 'string' && endpoint.match(GRAVE_SHOW_REGEX);
  if (!match) return false;
  const payloadSlug = getPayloadGraveSlug(payload);
  return Boolean(payloadSlug && payloadSlug !== match[1]);
};

const syncGraveCache = (endpoint, payload) => {
  if (!payload) return;
  const match = typeof endpoint === 'string' && endpoint.match(GRAVE_SHOW_REGEX);
  if (match) {
    if (shouldSkipGraveCache(endpoint, payload)) return;
    storeGraveInCache(match[1], payload.grave || payload);
  }
};

const sleep = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const pendingFetches = new Map();

const normalizeBodyForKey = (body) => {
  if (body == null) return '';
  if (typeof body === 'string') return body;
  if (body instanceof URLSearchParams) {
    return body.toString();
  }
  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    try {
      return JSON.stringify(Array.from(body.entries()));
    } catch {
      return '[object FormData]';
    }
  }
  if (ArrayBuffer.isView(body)) {
    return `${body.constructor?.name || 'TypedArray'}:${body.byteLength}`;
  }
  if (body instanceof ArrayBuffer) {
    return `ArrayBuffer:${body.byteLength}`;
  }
  try {
    return JSON.stringify(body);
  } catch {
    return String(body);
  }
};

const defaultDedupeKey = (input, init = {}) => {
  const explicitMethod = init?.method || (typeof input === 'object' && input?.method);
  const method = String(explicitMethod || 'GET').toUpperCase();
  const url =
    typeof input === 'string'
      ? input
      : input?.url || (typeof input === 'object' && 'href' in input ? input.href : null);
  if (!url) return null;
  const body = init?.body || (typeof input === 'object' && input?.body);
  const bodyKey = normalizeBodyForKey(body);
  return `${method} ${url} ${bodyKey}`;
};

const hasRequestHeader = (headers, name) => {
  if (!headers) return false;
  if (typeof headers.has === 'function') {
    return headers.has(name);
  }
  const normalizedName = String(name).toLowerCase();
  return Object.keys(headers).some((headerName) => headerName.toLowerCase() === normalizedName);
};

export { sanitizeMlsRequestInit };

const parseRetryAfterMs = (value) => {
  if (!value) return null;
  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return Math.max(0, numeric * 1000);
  }
  const parsedDate = Date.parse(value);
  if (!Number.isNaN(parsedDate)) {
    const diff = parsedDate - Date.now();
    return diff > 0 ? diff : 0;
  }
  return null;
};

const dispatchAuthGateOpen = (reason) => {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent('auth-gate:open', { detail: { reason } }));
  } catch {}
};

const leavePresenceBeforeLogout = async () => {
  const token = getToken();
  if (!token) return;

  const sessionId = getMessengerV2PresenceSessionId();
  if (!sessionId) return;

  try {
    await fetch_request('/api/presence/leave', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeader(),
      },
      body: JSON.stringify({
        session_id: sessionId,
      }),
    });
  } catch {}
};

const disconnectCableBeforeLogout = () => {
  realtimeRuntime.destroy();
};

const loadLegacyMlsConversationGuard = async () => {
  try {
    const module = await import('../e2ee/sessionManagerMLS.js');
    return {
      getKnownMLSConversation: module?.getKnownMLSConversation,
      isMLSConversation: module?.isMLSConversation,
    };
  } catch {
    return {
      getKnownMLSConversation: null,
      isMLSConversation: null,
    };
  }
};

const readRateLimitCode = (headers, data) => {
  let headerCode = null;
  if (headers) {
    if (typeof headers.get === 'function') {
      headerCode = headers.get('X-RateLimit-Code') || headers.get('x-ratelimit-code');
    } else {
      headerCode = headers['x-ratelimit-code'] || headers['X-RateLimit-Code'];
    }
  }
  let dataCode = null;
  if (data && typeof data === 'object') dataCode = data.code;
  if (!dataCode && typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      dataCode = parsed?.code;
    } catch {}
  }
  return dataCode || headerCode;
};

// Validate JWT structure "header.payload.signature"
export const getToken = () => {
  // Accept any non-empty token; backend may issue opaque tokens.
  // Prefer localStorage, but tolerate sessionStorage to handle legacy/session-based auth flows.
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  if (token && String(token).length > 0) {
    // If a token exists, the logout marker is stale and should not block silent refresh.
    try {
      sessionStorage.removeItem('ng.logout');
    } catch {}
    return token;
  }
  return null;
};

const decodeJwt = (t) => {
  try {
    const [, payload] = t.split('.');
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
};

export const isJwtExpiringSoon = (token, skewSeconds = 60) => {
  if (!token) return true;
  const data = decodeJwt(token);
  const expSec = Number(data?.exp);
  if (!Number.isFinite(expSec)) return false;
  return Math.floor(Date.now() / 1000) >= expSec - Math.max(0, Number(skewSeconds) || 0);
};

const notifyAuthTokenRefreshed = (token, reason = 'response') => {
  if (typeof window === 'undefined' || !token) return;
  const detail = { token, reason, at: Date.now() };
  try {
    window.dispatchEvent(new CustomEvent(AUTH_TOKEN_REFRESHED_EVENT, { detail }));
  } catch {}
  try {
    localStorage.setItem(
      AUTH_TOKEN_STORAGE_SYNC_KEY,
      JSON.stringify({ ...detail, nonce: Math.random().toString(36).slice(2) }),
    );
  } catch {}
};

const notifyAuthTokenRemoved = (reason = 'logout') => {
  if (typeof window === 'undefined') return;
  const detail = { reason, at: Date.now() };
  try {
    window.dispatchEvent(new CustomEvent(AUTH_TOKEN_REMOVED_EVENT, { detail }));
  } catch {}
  try {
    localStorage.setItem(
      AUTH_TOKEN_STORAGE_SYNC_KEY,
      JSON.stringify({ ...detail, token: null, nonce: Math.random().toString(36).slice(2) }),
    );
  } catch {}
};

// Return Authorization header if token is valid
export const authHeader = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Helper to persist token from response headers
const persistTokenFromResponse = (headers) => {
  if (!headers) return;
  const newToken = headers['x-authentication-token'] || headers.get?.('x-authentication-token');
  if (newToken) {
    const oldToken = getToken();
    localStorage.setItem('token', newToken);
    try {
      sessionStorage.removeItem('ng.logout');
    } catch {}
    scheduleAccessTokenRefresh(newToken);
    if (newToken !== oldToken) {
      notifyAuthTokenRefreshed(newToken);
    }
  }
};

const handleUnauthorized = () => {
  notifyAuthTokenRemoved('unauthorized');
  clearLocalStoragePreserveMls();
  clearSessionStoragePreserveMls();

  try {
    import('../features/messenger-v2/runtime/sessionManagerV2.js')
      .then((module) => {
        if (!getToken()) module?.clearSessionManagerV2Cache?.();
      })
      .catch(() => {});
  } catch {}

  try {
    import('../e2ee/sessionManagerMLS.js')
      .then((module) => module?.resetSessionManagerMLS?.({ teardown: true }))
      .catch(() => {});
  } catch {}

  try {
    import('../e2ee/mlsOrchestrator.js')
      .then((module) => module?.resetEnsureConversationReadyFlights?.())
      .catch(() => {});
  } catch {}

  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('mls:session-reset'));
    }
  } catch {}

  store.dispatch(setConfirmSignOut());
  store.dispatch(resetReduxStoreAction());
  // Avoid redirect loops on non-auth endpoints
  if (typeof window !== 'undefined') {
    try {
      const p = new URL(window.location.href);
      const isPublic = isPublicOrOptional(p.pathname);
      if (!isPublic) window.location.href = '/sign-in';
    } catch {
      window.location.href = '/sign-in';
    }
  }
};

const isPublicOrOptional = (fullUrlOrPath = '') => {
  try {
    const u = fullUrlOrPath.startsWith('http')
      ? new URL(fullUrlOrPath)
      : new URL(fullUrlOrPath, window.location.origin);
    const p = u.pathname || '';
    // Public search endpoints and public metadata
    const publicPrefixes = [
      '/api/search_graves',
      '/api/search_municipalities',
      '/api/search_cemeteries',
      '/api/graves_count',
      '/api/relations',
      '/api/relation_directions',
      '/api/countries',
      '/api/languages',
      '/api/municipalities_count',
      '/api/cemeteries_count',
      '/api/health',
      '/seo',
    ];
    if (publicPrefixes.some((pre) => p.startsWith(pre))) return true;
    // Optional auth checks that should not force redirect for guests
    const optionalExact = ['/api/users/me/profile'];
    if (optionalExact.includes(p)) return true;
    return false;
  } catch {
    return false;
  }
};

// Attempt a one-time silent refresh using refresh-token cookie
let refreshing = null;
const trySilentRefresh = async () => {
  try {
    if (sessionStorage.getItem('ng.logout') === '1') return null;
  } catch {}
  if (refreshing) return refreshing;
  const tokenBeforeLock = getToken();
  refreshing = (async () => {
    const refreshUnderLock = async () => {
      const currentToken = getToken();
      if (
        currentToken &&
        currentToken !== tokenBeforeLock &&
        !isJwtExpiringSoon(currentToken, 120)
      ) {
        return true;
      }

      try {
        const installationId = await getBrowserInstallationId();
        const res = await fetch(`${CONSTANTS.baseUrl}/api/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
          cache: 'no-store',
          headers: {
            'X-MLS-Device-Id': installationId,
          },
        });
        if (!res.ok) throw new Error(`Refresh failed: ${res.status}`);
        persistTokenFromResponse(res.headers);
        return true;
      } catch {
        return false;
      }
    };

    try {
      const lockManager =
        typeof navigator !== 'undefined' && navigator?.locks?.request ? navigator.locks : null;
      return lockManager
        ? await lockManager.request(
            'nettgrav-auth-refresh',
            { mode: 'exclusive' },
            refreshUnderLock,
          )
        : await refreshUnderLock();
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
};

// Proactive refresh timer based on JWT exp
let refreshTimer = null;

export const ensureFreshAuthToken = async ({ minTtlSeconds = 120 } = {}) => {
  const token = getToken();
  if (!token) return null;
  if (!isJwtExpiringSoon(token, minTtlSeconds)) return token;

  const refreshed = await trySilentRefresh();
  return refreshed ? getToken() : token;
};

export const scheduleAccessTokenRefresh = (token) => {
  try {
    if (refreshTimer) clearTimeout(refreshTimer);
  } catch {}
  const t = token || getToken();
  if (!t) return;
  const data = decodeJwt(t);
  const expSec = data?.exp;
  if (!expSec) return;
  const msUntil = expSec * 1000 - Date.now() - 90_000; // refresh 90s before
  if (msUntil <= 0) return;
  refreshTimer = setTimeout(
    () => {
      trySilentRefresh();
    },
    Math.min(msUntil, 24 * 60 * 60 * 1000),
  );
};

// Normalize a potentially absolute or malformed asset URL to a clean path.
// - Strips protocol/host
// - Deduplicates repeated "/protected/assets/..." sequences
// - Preserves query params (except duplicate token handling, done later)
const assetEndpoint = (assetPath) => {
  if (!assetPath) return '';

  // Pass through data/blob URLs untouched
  if (assetPath.startsWith('data:') || assetPath.startsWith('blob:')) return assetPath;

  let pathOnly = assetPath;
  try {
    const url = new URL(assetPath);
    pathOnly = `${url.pathname}${url.search}`;
  } catch {
    pathOnly = assetPath.startsWith('/') ? assetPath : `/${assetPath}`;
  }

  // If multiple protected paths got concatenated, keep the first full match
  if (pathOnly.includes('/protected/assets/')) {
    const match = pathOnly.match(/\/protected\/assets\/[^?\s]+(?:\?[^#\s]*)?/);
    if (match) pathOnly = match[0];
  }

  return pathOnly;
};

// Append a JWT token to asset paths so <img>/<video> can fetch protected assets
// without custom headers. If no token is present, returns the path unchanged.
const withAssetToken = (assetPath) => {
  if (!assetPath) return assetPath;

  const token = getToken();
  const removeToken = (value) =>
    value
      .replace(/([?&])token=[^&#]*(&|$)/, (match, prefix, suffix) => {
        if (suffix === '&') return prefix;
        return prefix === '?' ? '?' : '';
      })
      .replace(/[?&]$/, '');

  try {
    const parsed = new URL(assetPath, 'http://local');
    if (token) {
      parsed.searchParams.set('token', token);
    } else {
      parsed.searchParams.delete('token');
    }
    const query = parsed.searchParams.toString();
    return `${parsed.pathname}${query ? `?${query}` : ''}`;
  } catch {
    if (!token) {
      return removeToken(assetPath);
    }
    const basePath = removeToken(assetPath);
    const sep = basePath.includes('?') ? '&' : '?';
    return `${basePath}${sep}token=${encodeURIComponent(token)}`;
  }
};

const request = async (endpoint, params = {}, method = 'get', headers = {}, options = {}) => {
  await ensureFreshAuthToken({ minTtlSeconds: 60 }).catch(() => null);
  const installationId = await getBrowserInstallationId();
  const state = store.getState();
  const storedToken = getToken();
  const userId = state.user?.user?.id;
  const url = CONSTANTS.baseUrl + '/api' + endpoint;
  const isGet = String(method || 'get').toLowerCase() === 'get';
  const cacheKey = options.cacheKey || endpoint;
  const cacheTtlMs = Number.isFinite(options.cacheTtlMs)
    ? options.cacheTtlMs
    : CACHE_DEFAULT_TTL_MS;
  const isSearchEndpoint =
    typeof endpoint === 'string' &&
    (endpoint.startsWith('/search_graves') || endpoint.startsWith('/api/search_graves'));
  const wantCache =
    isGet && options.cache !== 'no-store' && options.cache !== 'reload' && !isSearchEndpoint;

  const config = {
    url,
    method,
    params: method === 'get' ? params : null,
    data: method !== 'get' ? params : null,
    headers: {
      'Content-Type': 'application/json',
      Accept: '*/*',
      ...headers,
      'X-MLS-Device-Id': installationId,
    },
    withCredentials: true,
  };
  const endpointPath = typeof endpoint === 'string' ? endpoint.split('?')[0] : '';
  const skipGlobalUnauthorizedHandler =
    endpointPath === '/sessions' ||
    endpointPath === '/sessions/send_phone_verification' ||
    endpointPath === '/sessions/verify_phone_code';

  let isApiCall = false;
  try {
    isApiCall = new URL(url).pathname.startsWith('/api/');
  } catch {
    isApiCall = typeof endpoint === 'string' && endpoint.startsWith('/api/');
  }

  if (storedToken) {
    config.headers.Authorization = `Bearer ${storedToken}`;
  }
  if (userId) {
    config.headers['X-User-Id'] = userId;
  }
  if (isApiCall) {
    const visitorToken = getVisitorToken();
    if (visitorToken) {
      config.headers['X-Visitor-Token'] = visitorToken;
    }
  }

  if (wantCache) {
    const cached = getCachedValue(cacheKey, cacheTtlMs);
    if (cached) {
      return {
        data: cached,
        status: 200,
        statusText: 'OK',
        headers: { 'x-cache': 'HIT' },
        config,
      };
    }
  }

  try {
    const response = await axios(config);
    persistTokenFromResponse(response.headers);
    if (wantCache && response?.data !== undefined) {
      if (
        !shouldSkipGraveCache(endpoint, response.data) &&
        !payloadHasSignedMediaUrl(response.data)
      ) {
        setCachedValue(cacheKey, response.data, cacheTtlMs);
      }
      syncGraveCache(endpoint, response.data);
    } else if (!isGet) {
      // Invalidate caches for mutating requests using both raw and /api-prefixed keys
      const invalidationKey = endpoint.split('?')[0] || '/api';
      const apiPrefixedKey = invalidationKey.startsWith('/api')
        ? invalidationKey
        : `/api${invalidationKey}`;

      invalidateCache(invalidationKey, { exact: false });
      if (apiPrefixedKey !== invalidationKey) {
        invalidateCache(apiPrefixedKey, { exact: false });
      }

      const match = endpoint.match(GRAVE_SHOW_REGEX);
      if (match) {
        invalidateGraveCache(match[1]);
      }
    }
    return response;
  } catch (error) {
    if (wantCache) {
      const cached = getCachedValue(cacheKey, cacheTtlMs);
      if (cached) {
        const cachedResponse = {
          data: cached,
          status: 200,
          statusText: 'OK (stale)',
          headers: { 'x-cache': 'STALE' },
          config,
        };
        syncGraveCache(endpoint, cached);
        return cachedResponse;
      }
    }
    if (error.response && error.response.status === 401) {
      if (skipGlobalUnauthorizedHandler) {
        throw error;
      }
      // Do not redirect on public/optional endpoints
      if (isPublicOrOptional(config.url)) {
        throw error;
      }
      if (!config._retried) {
        const ok = await trySilentRefresh();
        if (ok) {
          const retry = { ...config, _retried: true };
          const refreshedToken = getToken();
          if (refreshedToken) {
            retry.headers = {
              ...(retry.headers || {}),
              Authorization: `Bearer ${refreshedToken}`,
            };
          }
          const response = await axios(retry);
          persistTokenFromResponse(response.headers);
          return response;
        }
      }
      handleUnauthorized();
    }
    if (error.response && error.response.status === 429) {
      const code = readRateLimitCode(error.response.headers, error.response.data);
      if (code === 'guest_quota_exceeded') {
        dispatchAuthGateOpen(code);
      }
    }
    throw error;
  }
};

const fetch_request = async (endpoint, requestOptions = null) => {
  await ensureFreshAuthToken({ minTtlSeconds: 60 }).catch(() => null);
  const installationId = await getBrowserInstallationId();
  const state = store.getState();
  const storedToken = getToken();
  const userId = state.user?.user?.id;

  const options = {
    method: 'get',
    redirect: 'follow',
    ...(requestOptions || {}),
    credentials: 'include',
  };

  options.headers = {
    Accept: '*/*',
    ...(requestOptions?.headers || {}),
    'X-MLS-Device-Id': installationId,
  };

  if (typeof endpoint === 'string') {
    const method = String(options.method || 'GET').toUpperCase();
    const match = endpoint.match(/^\/api\/conversations\/([^/]+)\/messages$/);
    if (match && method === 'POST') {
      const conversationId = decodeURIComponent(match[1]);
      const { getKnownMLSConversation, isMLSConversation } = await loadLegacyMlsConversationGuard();
      const knownConversation =
        typeof getKnownMLSConversation === 'function'
          ? getKnownMLSConversation(conversationId)
          : null;
      if (
        knownConversation &&
        typeof isMLSConversation === 'function' &&
        isMLSConversation(knownConversation)
      ) {
        const error = new Error('Blocked plaintext send for MLS conversation');
        error.status = 400;
        error.code = 'E2EE_MLS_ONLY';
        if (typeof console !== 'undefined') {
          console.warn('[Guard] Prevented legacy POST /messages on MLS convo', {
            path: endpoint,
            conversationId,
          });
        }
        throw error;
      }
      if (!knownConversation && typeof console !== 'undefined') {
        if (typeof console.groupCollapsed === 'function') {
          console.groupCollapsed('[Trace] Legacy messages POST called');
        }
        if (typeof console.trace === 'function') {
          console.trace();
        }
        if (typeof console.groupEnd === 'function') {
          console.groupEnd();
        }
      }
    }
  }

  const needsAuth =
    endpoint.startsWith('/api/') ||
    endpoint.startsWith('/protected/') ||
    endpoint.startsWith('/e2ee/');
  let isApiCall = false;
  if (typeof endpoint === 'string') {
    try {
      isApiCall = new URL(endpoint, CONSTANTS.baseUrl).pathname.startsWith('/api/');
    } catch {
      isApiCall = endpoint.startsWith('/api/');
    }
  }
  if (needsAuth && storedToken && !hasRequestHeader(options.headers, 'authorization')) {
    options.headers.Authorization = `Bearer ${storedToken}`;
  }
  if (needsAuth && userId) {
    options.headers['X-User-Id'] = userId;
  }
  if (isApiCall) {
    const visitorToken = getVisitorToken();
    if (visitorToken) {
      options.headers['X-Visitor-Token'] = visitorToken;
    }
  }

  const timeoutMs = Number.isFinite(options.timeoutMs) ? options.timeoutMs : null;
  const externalSignal = options.signal;
  let timeoutId;
  let timeoutController;
  let abortHandler;

  if (timeoutMs && timeoutMs > 0) {
    timeoutController = new AbortController();
    if (externalSignal) {
      abortHandler = () => timeoutController.abort();
      if (externalSignal.aborted) {
        timeoutController.abort();
      } else {
        externalSignal.addEventListener('abort', abortHandler, { once: true });
      }
    }
    options.signal = timeoutController.signal;
    timeoutId = setTimeout(() => timeoutController.abort(), timeoutMs);
  }

  delete options.timeoutMs;

  const finalOptions = sanitizeMlsRequestInit(endpoint, options);

  const method = String(options.method || 'GET').toUpperCase();
  const cacheKey = options.cacheKey || endpoint;
  const cacheTtlMs = Number.isFinite(options.cacheTtlMs)
    ? options.cacheTtlMs
    : CACHE_DEFAULT_TTL_MS;
  const isSearchEndpoint =
    typeof endpoint === 'string' &&
    (endpoint.startsWith('/api/search_graves') || endpoint.startsWith('/search_graves'));
  const wantCache =
    method === 'GET' &&
    options.cache !== 'no-store' &&
    options.cache !== 'reload' &&
    !isSearchEndpoint;
  if (wantCache) {
    const cached = getCachedValue(cacheKey, cacheTtlMs);
    if (cached) {
      return buildCachedResponse(cached, { stale: false });
    }
  }

  let response;
  try {
    response = await fetch(CONSTANTS.baseUrl + endpoint, finalOptions);
    if (response.status === 304 && method === 'GET') {
      if (wantCache) {
        const cached = getCachedValue(cacheKey, cacheTtlMs);
        if (cached) {
          return buildCachedResponse(cached, { stale: false });
        }
      }

      response = await fetch(CONSTANTS.baseUrl + endpoint, {
        ...finalOptions,
        cache: 'no-store',
      });
    }
  } catch (error) {
    if (wantCache) {
      const cached = getCachedValue(cacheKey, cacheTtlMs);
      if (cached) {
        return buildCachedResponse(cached, { stale: true });
      }
    }
    throw error;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
    if (externalSignal && abortHandler) {
      externalSignal.removeEventListener('abort', abortHandler);
    }
  }

  persistTokenFromResponse(response.headers);
  if (wantCache) {
    if (response.ok) {
      try {
        const clone = response.clone();
        const payload = await clone.json();
        if (!payloadHasSignedMediaUrl(payload)) {
          setCachedValue(cacheKey, payload, cacheTtlMs);
        }
        syncGraveCache(endpoint, payload);
      } catch {
        // ignore cache write failures for non-JSON endpoints
      }
    }
  } else if (method !== 'GET') {
    const invalidationKey = endpoint.split('?')[0];
    invalidateCache(invalidationKey || '/api', { exact: false });
    const match = endpoint.match(GRAVE_SHOW_REGEX);
    if (match) {
      invalidateGraveCache(match[1]);
    }
  }

  if (
    response.status >= 400 &&
    typeof endpoint === 'string' &&
    /^\/api\/v1\/mls\/groups\/[^/]+\/messages/.test(endpoint)
  ) {
    try {
      const cloned = response.clone();
      const text = await cloned.text();
      const trimmed = typeof text === 'string' ? text.slice(0, 300) : '';
      if (typeof console !== 'undefined' && typeof console.info === 'function') {
        console.info('[MLS] POST /mls/groups/*/messages failure', {
          status: response.status,
          method: String(finalOptions.method || 'GET').toUpperCase(),
          text: trimmed,
        });
      }
    } catch (logError) {
      if (typeof console !== 'undefined' && typeof console.info === 'function') {
        console.info('[MLS] POST /mls/groups/*/messages failure', {
          status: response.status,
          method: String(finalOptions.method || 'GET').toUpperCase(),
          text: null,
          error: logError,
        });
      }
    }
  }

  if (response.status === 401) {
    if (!isPublicOrOptional(CONSTANTS.baseUrl + endpoint)) {
      // Try silent refresh once for fetch-based calls as well
      const ok = await trySilentRefresh();
      if (ok) {
        const refreshedToken = getToken();
        const retryOptions = {
          ...finalOptions,
          headers: {
            ...(finalOptions.headers || {}),
            ...(refreshedToken ? { Authorization: `Bearer ${refreshedToken}` } : {}),
          },
        };
        const retryResponse = await fetch(CONSTANTS.baseUrl + endpoint, retryOptions);
        persistTokenFromResponse(retryResponse.headers);
        return retryResponse;
      }
      handleUnauthorized();
    }
  }
  if (response.status === 429) {
    let code = readRateLimitCode(response.headers);
    if (!code) {
      try {
        const payload = await response.clone().json();
        code = readRateLimitCode(response.headers, payload);
      } catch {}
    }
    if (code === 'guest_quota_exceeded') {
      dispatchAuthGateOpen(code);
    }
  }
  return response;
};

let visitorTokenPromise = null;
const initVisitorToken = async () => {
  if (typeof window === 'undefined') return null;
  const existing = getVisitorToken();
  if (existing) return existing;
  if (visitorTokenPromise) return visitorTokenPromise;
  visitorTokenPromise = (async () => {
    try {
      const response = await fetch(`${CONSTANTS.baseUrl}/api/visitor_token`, {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;
      const payload = await response.json();
      if (payload?.token) {
        setVisitorToken(payload.token);
        return payload.token;
      }
    } catch {}
    return null;
  })();

  try {
    return await visitorTokenPromise;
  } finally {
    visitorTokenPromise = null;
  }
};

export const fetchWithRetry = (input, init = {}, options = {}) => {
  const { retries = 6, baseDelayMs = 1500, maxDelayMs = 120000, dedupeKey } = options;
  const key = dedupeKey ?? defaultDedupeKey(input, init);

  if (key) {
    const pending = pendingFetches.get(key);
    if (pending) {
      return pending.clone();
    }
  }

  let sharedResponsePromise;
  let basePromise;

  const ensureSharedResponse = () => {
    if (!sharedResponsePromise) {
      sharedResponsePromise = basePromise.then((response) => response.clone());
    }
    return sharedResponsePromise;
  };

  const execute = async () => {
    let attempt = 0;
    let delay = baseDelayMs;

    // Always attempt the request at least once
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const response = await fetch_request(input, init);
      if (response.status !== 429 && response.status !== 503) {
        return response;
      }

      if (attempt >= retries) {
        return response;
      }

      const retryAfterHeader =
        response.headers?.get?.('Retry-After') || response.headers?.get?.('retry-after');
      const retryAfterMs = parseRetryAfterMs(retryAfterHeader);
      const baseWait = Number.isFinite(retryAfterMs) && retryAfterMs > 0 ? retryAfterMs : delay;
      const clampedBase = Math.min(baseWait, maxDelayMs);
      const jitter = clampedBase > 0 ? Math.random() * clampedBase * 0.3 : 0;

      if (clampedBase + jitter > 0) {
        await sleep(clampedBase + jitter);
      }

      attempt += 1;
      delay = Math.min(delay * 2, maxDelayMs);
    }
  };

  basePromise = execute();

  if (!key) {
    return basePromise;
  }

  pendingFetches.set(key, {
    clone: () => ensureSharedResponse().then((response) => response.clone()),
  });

  basePromise.finally(() => {
    pendingFetches.delete(key);
  });

  return basePromise.then(async (response) => {
    await ensureSharedResponse();
    return response;
  });
};

export {
  assetEndpoint,
  fetch_request,
  initVisitorToken,
  persistTokenFromResponse,
  request,
  trySilentRefresh,
  withAssetToken,
};

// Compose a fully-qualified, tokenized URL for protected assets
export const protectedUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;

  let baseOrigin = null;
  try {
    baseOrigin = new URL(CONSTANTS.baseUrl).origin;
  } catch {
    baseOrigin = null;
  }

  let parsed;
  try {
    parsed = new URL(url, CONSTANTS.baseUrl);
  } catch {
    return url;
  }

  // Only append auth tokens for protected assets
  if (!parsed.pathname.includes('/protected/assets/')) {
    return parsed.toString();
  }

  // Protected assets must go through the API host for auth/redirect.
  if (baseOrigin && parsed.origin !== baseOrigin) {
    parsed = new URL(`${baseOrigin}${parsed.pathname}${parsed.search}`);
  }

  parsed.searchParams.delete('token');
  const token = getToken();
  if (token) {
    parsed.searchParams.set('token', token);
  }

  return parsed.toString();
};

// Explicit logout with server-side refresh-token revocation
export const revokeSession = async () => {
  try {
    await leavePresenceBeforeLogout();
  } catch {}
  disconnectCableBeforeLogout();
  try {
    const module = await import(
      '../features/messenger-v2/runtime/webPushSubscriptionService.js'
    );
    await module?.removeMessengerV2WebPushSubscription?.();
  } catch {}
  try {
    await fetch_request('/api/v1/mls/device_identity', {
      method: 'DELETE',
      headers: { ...authHeader() },
    });
  } catch {}
  try {
    const url = `${CONSTANTS.baseUrl}/api/auth/logout`;
    const installationId = await getBrowserInstallationId();
    await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-MLS-Device-Id': installationId },
    });
  } catch {}
  try {
    // Clear client-side auth artifacts so Redux re-init cannot mark the user as signed in.
    notifyAuthTokenRemoved('logout');
    localStorage.removeItem('token');
    localStorage.removeItem('auth');
    localStorage.removeItem('user-slug');
    localStorage.removeItem('user-slag');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('auth');
    sessionStorage.removeItem('user-slug');
    sessionStorage.removeItem('user-slag');
  } finally {
    try {
      sessionStorage.setItem('ng.logout', '1');
    } catch {}
  }
  try {
    const module = await import('../features/messenger-v2/runtime/sessionManagerV2.js');
    module?.clearSessionManagerV2Cache?.();
  } catch {}
  try {
    const module = await import('../e2ee/sessionManagerMLS.js');
    await module?.resetSessionManagerMLS?.({ teardown: true });
  } catch {}
  try {
    const module = await import('../e2ee/mlsOrchestrator.js');
    module?.resetEnsureConversationReadyFlights?.();
  } catch {}
};

const PRIVATE_AFTER_LOGOUT_PREFIXES = [
  '/admin',
  '/settings',
  '/members/',
  '/members/me',
  '/family-tree',
  '/notifications',
  '/messages',
  '/groups',
  getRoomsBasePath(),
  '/vipps-payment-confirmation',
  '/vipps-agreements',
  '/create_grave',
  '/graves/new',
];

const isPrivateAfterLogoutPath = (pathname = '') => {
  if (!pathname) return false;
  return PRIVATE_AFTER_LOGOUT_PREFIXES.some((prefix) => pathname.startsWith(prefix));
};

export const resolvePostLogoutRedirect = (pathname, search = '', hash = '') => {
  const path = pathname || (typeof window !== 'undefined' ? window.location.pathname : '/');
  const qs = search || '';
  const hs = hash || '';
  const current = `${path}${qs}${hs}`;

  if (isPrivateAfterLogoutPath(path)) {
    return `/sign-in?next=${encodeURIComponent(current)}`;
  }

  return current;
};

// Schedule on load
try {
  scheduleAccessTokenRefresh();
} catch {}
